import { useCallback } from "react";

import { createChair, listChairs, updateChair } from "../api/appointments";
import {
  acceptStarterBlockReasons,
  createBlockReason,
  listBlockReasons,
  removeBlockReason,
  updateBlockReason,
} from "../api/block-reasons";
import { listBranchHours, setBranchHours } from "../api/branches";
import { useCan } from "../auth/use-can";
import { BlockReasonsPanel } from "./block-reasons-panel";
import { ChairsPanel } from "./chairs-panel";
import { HoursPanel } from "./hours-panel";
import { NoScope, PaneHead, scopeProblem, useScopedResource } from "./pane";
import { useSettingsScope } from "./settings-layout";
import { Toast } from "./toast";
import { useWriter } from "./use-writer";

/**
 * The three panes scoped to one location — *Manage clinic* in the rail.
 *
 * **Procedures & prices left for `/plans/procedures`** on 2026-09-23 (PL1), so a
 * dentist building a plan can read the list; it keeps `CataloguePanel` from here.
 *
 * **These are the five settings nobody could find.** Opening hours, chairs,
 * procedures, prices and block reasons were all inside `branch-page.tsx`,
 * reached by opening a location and scrolling, and named nowhere on the
 * Settings index. They are the client's own list from QA item 10: *"being able
 * to modify or change anything (chairs...)"*.
 *
 * **Which location they show comes from the rail's switcher**, which writes the
 * one active branch this application has — see `settings-layout.tsx` on why
 * that also changes the calendar, and why a Settings-local scope would need an
 * API change rather than a front-end one.
 *
 * Each pane loads exactly what it renders. That is the point of splitting them:
 * `branch-page.tsx` held five reads and five reload callbacks for content that
 * was never on screen at the same time, so opening it fetched the procedure
 * catalogue whether or not anybody scrolled to it.
 */

/**
 * The state every pane here has to handle before it can show anything: no
 * locations at all, none chosen yet, or one this admin is not rostered to
 * (decision S7 — `BranchScopeGuard` admits on `branch_memberships`, not role).
 */
function useLocationScope(): {
  scope: string | undefined;
  problem: "none" | "unchosen" | "not-a-member" | null;
  branchId: string | null;
} {
  const { branches, branch } = useSettingsScope();
  return {
    scope: branch?.name,
    problem: scopeProblem(branches, branch),
    branchId: branch?.id ?? null,
  };
}

/**
 * When this location's day starts and ends, per weekday.
 *
 * **Clinic-scoped on the wire, unlike the three below** — hours need no
 * membership (decision S7), so a location this admin is not rostered to still
 * has an editable week. That is why this one asks `problem !== "not-a-member"`
 * rather than `problem === null`.
 */
export function HoursPane(): React.JSX.Element {
  const { scope, problem, branchId } = useLocationScope();
  const { busy, toast, dismiss, fail, run } = useWriter();

  const load = useCallback(
    async (signal?: AbortSignal) => (branchId === null ? [] : listBranchHours(branchId, signal)),
    [branchId],
  );

  const { data: hours, reload } = useScopedResource(
    load,
    "Could not read this location's opening hours.",
    fail,
    branchId !== null,
    branchId,
  );

  return (
    <>
      <PaneHead title="Opening hours" scope={scope} />

      {problem !== null && problem !== "not-a-member" ? (
        <NoScope what="opening hours" reason={problem} />
      ) : (
        <HoursPanel
          hours={hours}
          busy={busy}
          onSave={(days) => {
            if (branchId === null) return;
            void run(() => setBranchHours(branchId, days), reload, "Opening hours saved.");
          }}
        />
      )}

      {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
    </>
  );
}

/** The treatment rooms in this location, in the order the calendar draws them. */
export function ChairsPane(): React.JSX.Element {
  const { scope, problem, branchId } = useLocationScope();
  const { busy, toast, dismiss, fail, run } = useWriter();

  const load = useCallback((signal?: AbortSignal) => listChairs(signal), []);

  const { data: chairs, reload } = useScopedResource(
    load,
    "Could not read this location's chairs.",
    fail,
    problem === null,
    branchId,
  );

  return (
    <>
      <PaneHead title="Chairs" scope={scope} />

      {problem !== null ? (
        <NoScope what="chairs" reason={problem} />
      ) : (
        <ChairsPanel
          chairs={chairs}
          busy={busy}
          onCreate={(body) => {
            void run(() => createChair(body), reload, "Chair added.");
          }}
          onUpdate={(id, body, done) => {
            void run(() => updateChair(id, body), reload, done);
          }}
          onReorder={(ordered) => {
            void run(
              async () => {
                // **A whole new left-to-right order, not a swap of two values.**
                // Chairs added before this screen existed never had a
                // `sortOrder` written, so a branch can hold several at the same
                // value, tie-broken by name — and exchanging two equal numbers
                // reorders nothing. Sequential, and only the rows that moved.
                for (const [index, chair] of ordered.entries()) {
                  if (chair.sortOrder !== index) await updateChair(chair.id, { sortOrder: index });
                }
              },
              reload,
              "Order saved.",
            );
          }}
        />
      )}

      {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
    </>
  );
}

/** Why a chair is held when nobody is in it. */
export function BlockReasonsPane(): React.JSX.Element {
  const { scope, problem, branchId } = useLocationScope();
  const { busy, toast, dismiss, fail, run } = useWriter();
  const can = useCan();

  const load = useCallback((signal?: AbortSignal) => listBlockReasons(true, signal), []);

  const { data: reasons, reload } = useScopedResource(
    load,
    "Could not read this location's block reasons.",
    fail,
    problem === null,
    branchId,
  );

  return (
    <>
      <PaneHead title="Block reasons" scope={scope} />

      {problem !== null ? (
        <NoScope what="block reasons" reason={problem} />
      ) : (
        <BlockReasonsPanel
          reasons={reasons}
          isAdmin={can("settings.locations.manage")}
          busy={busy}
          onCreate={(body) => {
            void run(() => createBlockReason(body), reload, "Reason added.");
          }}
          onUpdate={(id, body, done) => {
            void run(() => updateBlockReason(id, body), reload, done);
          }}
          onRemove={(id, label) => {
            void run(() => removeBlockReason(id), reload, `${label} removed.`);
          }}
          onAcceptStarter={() => {
            void run(
              () => acceptStarterBlockReasons(),
              reload,
              "Added. They are your rows now — rename or remove any of them.",
            );
          }}
        />
      )}

      {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
    </>
  );
}
